import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import { AllCreators } from './AllCreators';
import HomeExamCards from './HomeExamCards';
import TestSeriesCards from './TestSeriesCards';
import SubjectWiseCards from './SubjectWiseCards';
import Exams from './Exams';
import SEO from '../common/SEO';

export default function LandingPage() {
  const [activeTab, setActiveTab] = useState('all');

  const homepageSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "My Exam World",
    "url": "https://www.myexamworld.com/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.myexamworld.com/alltest?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const features = [
    // Student features
    { icon: '📚', title: 'Practice Tests', desc: 'Access thousands of practice questions', type: 'student' },
    { icon: '📊', title: 'Performance Analytics', desc: 'Track progress with detailed reports', type: 'student' },
    { icon: '⏱️', title: 'Timed Exams', desc: 'Simulate real exam conditions', type: 'student' },
    { icon: '🎯', title: 'Smart Recommendations', desc: 'Personalized test suggestions', type: 'student' },

    // Creator features
    { icon: '🛠️', title: 'Test Builder', desc: 'Create exams with our intuitive editor', type: 'creator' },
    { icon: '👥', title: 'Class Management', desc: 'Organize students and track progress', type: 'creator' },
    { icon: '📈', title: 'Advanced Analytics', desc: 'Detailed student performance insights', type: 'creator' },
    { icon: '💰', title: 'Monetization', desc: 'Earn from your test creations', type: 'creator' },

    // Shared features
    { icon: '🏆', title: 'Leaderboards', desc: 'Compete with peers', type: 'shared' },
    { icon: '📅', title: 'Study Planner', desc: 'Personalized learning schedule', type: 'shared' },
    { icon: '🤝', title: 'Community', desc: 'Connect with learners and educators', type: 'shared' },
    { icon: '📱', title: 'Mobile Friendly', desc: 'Learn and create on any device', type: 'shared' }
  ];

  const filteredFeatures = activeTab === 'all'
    ? features
    : features.filter(feature => feature.type === activeTab || feature.type === 'shared');

  return (
    <div className="landing-page">
      <SEO
        title="My Exam World | Online Exam Portal, Test Series & Mock Tests"
        description="My Exam World is a multilingual online exam platform with live proctoring, mock tests, performance analytics, and custom test series for competitive exams."
        keywords="online exam platform, MPSC test series, SSC CGL mock test, RRB Group D practice paper, online quiz builder, education portal"
        url="/"
        schema={homepageSchema}
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Transform Your Exam Preparation</h1>
          <p>For students taking tests and educators creating them</p>
          <div className="hero-buttons">
            <Link to="/alltest" className="btn-light">Explore Tests</Link>
            <Link to="/signup" className="btn-dark">Get Started</Link>
          </div>
        </div>
      </section>

      {/* User Type Selection */}
      <section className="user-types">
        <div className="section-header">
          <h2>Designed For</h2>
          <p>Whether you're learning or teaching, we've got you covered</p>
        </div>
        <div className="type-cards">
          <div className="type-card student-card">
            <h3>Student</h3>
            <p className="card-subtitle">Everything you need to ace your exams</p>
            <ul className="benefits-list">
              <li>Unlimited practice tests</li>
              <li>Detailed performance reports</li>
              {/* <li>Stay motivated with daily quizzes and challenges</li> */}
              <li>Competitive leaderboards</li>
            </ul>
            <Link to="/alltest" className="type-cta">Start Learning →</Link>
          </div>
          <div className="type-card creator-card">
            <h3>Educator/Creator</h3>
            <p className="card-subtitle">Powerful tools to create and manage tests</p>
            <ul className="benefits-list">
              <li>Easy test builder</li>
              <li>Multi-language support</li>
              <li>Student performance analytics</li>
              {/* <li>Classroom management</li> */}
            </ul>
            <Link to="/signup" className="type-cta">Start Creating →</Link>
          </div>
        </div>
      </section>

      <TestSeriesCards />
      <SubjectWiseCards />
      {/* All Exams Section */}
      <section className="exams-section">
        <div className="section-header">
          <h2>Top Exams</h2>
          <p>Explore the most popular exams and start practicing today</p>
        </div>
        <HomeExamCards /> {/* Display a limited number of exams */}
        <div className="view-all">
          <Link to="/alltest" className=" btn-dark">View All Exams →</Link>
        </div>
      </section>
      <Exams limit={true} />

      {/* Features Section */}
      <section className="features">
        <div className="section-header">
          <h2>Powerful Features</h2>
          <p>Everything you need for effective testing and learning</p>
        </div>

        <div className="feature-tabs">
          <button
            className={`feature-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Features
          </button>

          <button
            className={`feature-tab ${activeTab === 'creator' ? 'active' : ''}`}
            onClick={() => setActiveTab('creator')}
          >
            For Creators
          </button>
          <button
            className={`feature-tab ${activeTab === 'student' ? 'active' : ''}`}
            onClick={() => setActiveTab('student')}
          >
            For Students
          </button>
        </div>

        <div className="feature-grid">
          {filteredFeatures.map((feature, index) => (
            <div
              key={index}
              className={`feature-card ${feature.type === 'creator' ? 'creator-feature' :
                feature.type === 'student' ? 'student-feature' :
                  'shared-feature'
                }`}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
              {feature.type === 'creator' && <div className="feature-badge creator">For Creators</div>}
              {feature.type === 'student' && <div className="feature-badge student">For Students</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Top Creators Section */}
      <section className="creators-section">
        <AllCreators limit={5} />
        <div className="view-all">
          <Link to="/allcreators" className="view-all-link">View All Creators →</Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="cta-content">
          <h2>Ready to transform your learning?</h2>
          <p>Join our community of learners and educators today</p>
          <div className="cta-buttons">
            <Link to="/signup" className="btn-light">Sign Up as Student</Link>
            <Link to="/signup" className="btn-dark">Sign Up as Creator</Link>
          </div>
        </div>
      </section>
    </div>
  );
}